import { motion } from 'motion/react';
import { useNavigate } from '@tanstack/react-router';
import { QuestionRenderer } from '../question-types/QuestionRenderer';
import type { Question } from '@/types';
import styles from './QuizReviewView.module.scss';

interface QuizReviewViewProps {
  quizName: string;
  prompt: string;
  questions: Question[];
  onBack?: () => void;
  backLink?: string;
  backLabel?: string;
}

export function QuizReviewView({
  quizName,
  prompt,
  questions,
  onBack,
  backLink = '/review-center',
  backLabel = 'Back to Review Center',
}: QuizReviewViewProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate({ to: backLink as any });
    }
  };

  return (
    <div className={styles.reviewView}>
      <div className={styles.reviewView__container}>
        <motion.header
          className={styles.reviewView__header}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button
            className={styles.reviewView__backButton}
            onClick={handleBack}
          >
            <BackArrow />
            {backLabel}
          </button>
          <h1 className={styles.reviewView__title}>{quizName}</h1>
          <p className={styles.reviewView__prompt}>{prompt}</p>
          <div className={styles.reviewView__meta}>
            <span>{questions.length} questions</span>
            <span className={styles.reviewView__metaDivider}>|</span>
            <span>All answers visible</span>
          </div>
        </motion.header>

        <div className={styles.reviewView__questions}>
          {questions.map((question, index) => (
            <motion.div
              key={question.id}
              className={styles.reviewView__questionCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <div className={styles.reviewView__questionNumber}>
                Question {index + 1}
              </div>
              <QuestionRenderer
                question={question}
                userAnswer={undefined}
                onAnswer={() => {}} // No-op in review mode
                showExplanation={true}
                isReviewMode={true}
              />
            </motion.div>
          ))}
        </div>

        <div className={styles.reviewView__footer}>
          <button
            className={styles.reviewView__footerButton}
            onClick={handleBack}
          >
            <BackArrow />
            {backLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function BackArrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M10 12L6 8L10 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
